import React, { useMemo, useState } from 'react';
import { Button, message, Table, Select } from 'antd';
import type { TableColumnsType } from 'antd';
import VehicleModelFormDrawer from './components/VehicleModelFormDrawer';
import { useQuery, useMutation } from '@apollo/client';
import { GET_ALL_VEHICLE_TYPES } from '../../../graphql/queries/getAllVehicleTypes';
import { CREATE_VEHICLE_TYPE, UPDATE_VEHICLE_TYPE } from './graphql/mutations/vehicleModelMutation';
import { VehicleTypePageType } from './types/VehicleModelType';
import { formatNumberWithCommas } from '../../../utils/numberUtils';
import { useTranslation } from 'react-i18next';
import TablePaginationBar from '../../../components/TablePaginationBar';

interface StatusOption {
    label: string;
    value: number;
}

const VehicleModelPage: React.FC = () => {
    const [drawerVisible, setDrawerVisible] = useState(false);
    const [selectedModel, setSelectedModel] = useState<VehicleTypePageType | null>(null);
    const [drawerMode, setDrawerMode] = useState<'create' | 'edit'>('create');
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);

    const { data, loading, refetch } = useQuery(GET_ALL_VEHICLE_TYPES, {
        fetchPolicy: 'network-only',
    });

    const vehicleTypes = data?.allVehicleTypes || [];
    const pagedData = useMemo(() => {
        const start = (currentPage - 1) * pageSize;
        const end = start + pageSize;
        return vehicleTypes.slice(start, end);
    }, [vehicleTypes, currentPage, pageSize]);

    const [createVehicleType] = useMutation(CREATE_VEHICLE_TYPE);
    const [updateVehicleType] = useMutation(UPDATE_VEHICLE_TYPE);

    const { t, i18n } = useTranslation();
    const statusOptions: StatusOption[] = [
        { label: t('enum.activeStatus.active'), value: 1 },
        { label: t('enum.activeStatus.suspended'), value: 0 },
    ];

    const getLocaleName = (record: VehicleTypePageType): string => {
        if (i18n.language === 'zh') return record.nameZh;
        if (i18n.language === 'es') return record.nameEs;
        return record.nameEn;
    };

    const handleStatusChange = async (record: VehicleTypePageType, newStatus: number) => {
        try {
            const input = {
                id: Number(record.id),
                nameEn: record.nameEn,
                nameZh: record.nameZh,
                nameEs: record.nameEs,
                status: newStatus,
                defaultMaxWeightLbs: record.defaultMaxWeightLbs,
                defaultMaxVolumeFt3: record.defaultMaxVolumeFt3,
                defaultLengthFt: record.defaultLengthFt,
                defaultWidthFt: record.defaultWidthFt,
                defaultHeightFt: record.defaultHeightFt,
            };
            await updateVehicleType({ variables: { input } });
            message.success(t('central.vehicleModel.messages.statusUpdatedSuccessfully') as string);
            await refetch();
        } catch (error) {
            message.error(t('central.vehicleModel.messages.failedToUpdateStatus') as string);
            console.error('Error updating status:', error);
        }
    };

    const showNewModelDrawer = () => {
        setSelectedModel(null);
        setDrawerMode('create');
        setDrawerVisible(true);
    };

    const showEditDrawer = (record: VehicleTypePageType) => {
        setSelectedModel(record);
        setDrawerMode('edit');
        setDrawerVisible(true);
    };

    const handleModelSubmit = async (values: VehicleTypePageType) => {
        const input = {
            ...values,
            status: Number(values.status),
            defaultMaxWeightLbs: values.defaultMaxWeightLbs != null ? Number(values.defaultMaxWeightLbs) : undefined,
            defaultMaxVolumeFt3: values.defaultMaxVolumeFt3 != null ? Number(values.defaultMaxVolumeFt3) : undefined,
            defaultLengthFt: values.defaultLengthFt != null ? Number(values.defaultLengthFt) : undefined,
            defaultWidthFt: values.defaultWidthFt != null ? Number(values.defaultWidthFt) : undefined,
            defaultHeightFt: values.defaultHeightFt != null ? Number(values.defaultHeightFt) : undefined,
        };
        try {
            if (drawerMode === 'edit' && selectedModel?.id) {
                await updateVehicleType({ variables: { input: { ...input, id: Number(selectedModel.id) } } });
                message.success(t('central.vehicleModel.messages.vehicleModelUpdatedSuccessfully') as string);
            } else {
                await createVehicleType({ variables: { input } });
                message.success(t('central.vehicleModel.messages.vehicleModelCreatedSuccessfully') as string);
            }
            setDrawerVisible(false);
            await refetch();
        } catch (error: any) {
            console.error(t('central.vehicleModel.messages.vehicleModelCreatedUpdatedFailed') as string, error);
            const gqlErr = error?.graphQLErrors?.[0];
            const messageKey: string | undefined = gqlErr?.extensions?.messageKey;
            message.error(messageKey ? t(messageKey) as string : t('central.vehicleModel.messages.vehicleModelCreatedUpdatedFailed') as string);
        }
    };

    const columns: TableColumnsType<VehicleTypePageType> = [
        {
            title: t('central.vehicleModel.columns.model'),
            key: 'name',
            align: 'left' as const,
            ellipsis: false,
            render: (_: unknown, record: VehicleTypePageType) => (
                <Button type="link" onClick={() => showEditDrawer(record)} style={{ paddingLeft: 0 }}>
                    <span style={{ whiteSpace: 'nowrap' }}>{getLocaleName(record)}</span>
                </Button>
            ),
        },
        {
            title: t('central.vehicleModel.columns.payload'),
            dataIndex: 'defaultMaxWeightLbs',
            key: 'defaultMaxWeightLbs',
            align: 'left' as const,
            ellipsis: false,
            render: (value: number) => {
                const text = formatNumberWithCommas(value);
                return <span style={{ whiteSpace: 'nowrap' }}>{text}</span>;
            }
        },
        {
            title: t('central.vehicleModel.columns.capacity'),
            dataIndex: 'defaultMaxVolumeFt3',
            key: 'defaultMaxVolumeFt3',
            align: 'left' as const,
            ellipsis: false,
            render: (value: number) => {
                const text = formatNumberWithCommas(value);
                return <span style={{ whiteSpace: 'nowrap' }}>{text}</span>;
            }
        },
        {
            title: t('central.vehicleModel.columns.length'),
            dataIndex: 'defaultLengthFt',
            key: 'defaultLengthFt',
            align: 'left' as const,
            ellipsis: false,
            render: (value: number) => {
                const text = formatNumberWithCommas(value);
                return <span style={{ whiteSpace: 'nowrap' }}>{text}</span>;
            }
        },
        {
            title: t('central.vehicleModel.columns.width'),
            dataIndex: 'defaultWidthFt',
            key: 'defaultWidthFt',
            align: 'left' as const,
            ellipsis: false,
            render: (value: number) => {
                const text = formatNumberWithCommas(value);
                return <span style={{ whiteSpace: 'nowrap' }}>{text}</span>;
            }
        },
        {
            title: t('central.vehicleModel.columns.height'),
            dataIndex: 'defaultHeightFt',
            key: 'defaultHeightFt',
            align: 'left' as const,
            ellipsis: false,
            render: (value: number) => {
                const text = formatNumberWithCommas(value);
                return <span style={{ whiteSpace: 'nowrap' }}>{text}</span>;
            }
        },
        {
            title: t('central.vehicleModel.columns.status'),
            dataIndex: 'status',
            key: 'status',
            align: 'left' as const,
            render: (status: number, record: VehicleTypePageType) => (
                <Select
                    value={status}
                    options={statusOptions}
                    style={{ width: 100 }}
                    onChange={(newStatus) => handleStatusChange(record, newStatus)}
                    size="small"
                />
            ),
        },
    ];

    return (
        <div className="bg-white rounded-lg shadow-sm px-3 pb-3 pt-0">
            <div className="flex justify-end gap-4 mb-3 pl-0">
                <Button
                    type="primary"
                    onClick={showNewModelDrawer}
                    className="w-auto min-w-24"
                >
                    <span className="font-bold">{t('components.button.new')}</span>
                </Button>
            </div>
            <Table
                loading={loading}
                columns={columns}
                dataSource={pagedData}
                pagination={false}
                rowKey="id"
                scroll={{x: 'max-content'}}
                size="middle"
                tableLayout="auto"
                className="mb-4"
            />
            <TablePaginationBar
                total={vehicleTypes.length}
                current={currentPage}
                pageSize={pageSize}
                onChange={(page, size) => {
                    setCurrentPage(page);
                    if (size && size !== pageSize) {
                        setPageSize(size);
                        setCurrentPage(1);
                    }
                }}
                onShowSizeChange={(_, size) => {
                    setPageSize(size);
                    setCurrentPage(1);
                }}
                pageSizeOptions={['10', '20', '50', '100']}
            />
            <VehicleModelFormDrawer
                visible={drawerVisible}
                onClose={() => setDrawerVisible(false)}
                onSubmit={handleModelSubmit}
                initialValues={selectedModel || undefined}
                mode={drawerMode}
                statusOptions={statusOptions}
                vehicleTypes={vehicleTypes}
            />
        </div>
    );
};

export default VehicleModelPage;
